/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';

import { COLORS, withAlpha } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';

import type { ProgressAltScrollTarget } from './progressAltScrollLib';

const HANDLE_SIZE_PX = 22;

const BAR_STYLE = css`
  label: PROGRESS_ALT_SCROLL_BAR_STYLE;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

/** The beam runs the full travel, exactly like the jog shuttle's, with the handle riding it. */
const TRACK_STYLE = css`
  position: relative;
  flex: 1;
  min-width: 0;
  height: ${HANDLE_SIZE_PX}px;
  cursor: pointer;
`;

const BEAM_STYLE = css`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  transform: translateY(-50%);
  background: ${withAlpha(COLORS.stroke, 0.9)};
`;

const HANDLE_STYLE = css`
  position: absolute;
  top: 0;
  width: ${HANDLE_SIZE_PX}px;
  height: ${HANDLE_SIZE_PX}px;
  border: 1px solid ${COLORS.stroke};
  border-radius: 50%;
  background: ${COLORS.white};
  box-shadow: 0 1px 2px ${withAlpha(COLORS.ink, 0.16)};
  cursor: grab;
  touch-action: none;

  &:hover {
    border-color: ${COLORS.midtone};
  }

  &:active {
    cursor: grabbing;
  }
`;

type ScrollMetrics = { scrollLeft: number; scrollWidth: number; clientWidth: number };

const EMPTY_METRICS: ScrollMetrics = { scrollLeft: 0, scrollWidth: 0, clientWidth: 0 };

/**
 * Horizontal scrollbar for the Progress Alt grid, in the jog shuttle's visual language: a beam
 * spanning the whole travel with a round handle riding it.
 *
 * The grid scrolls natively — this only reflects and drives `scrollLeft`, so keyboard, trackpad and
 * wheel scrolling keep working untouched. It hides itself when there is nothing to scroll.
 */
export const ProgressAltScrollBar = ({ scrollTarget, insetLeftPx, insetRightPx }: {
  /** The grid's scroll container, held as a target because scrolling writes through its ref. */
  scrollTarget: ProgressAltScrollTarget;
  /** Insets that line the beam's travel up with the grid's own content edges. */
  insetLeftPx: number;
  insetRightPx: number;
}) => {
  const { ref: scrollElementRef, token: scrollToken } = scrollTarget;
  const trackRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<ScrollMetrics>(EMPTY_METRICS);

  useEffect(() => {
    const element = scrollElementRef.current;
    if (!element) return undefined;

    const readMetrics = () =>
      setMetrics({
        scrollLeft: element.scrollLeft,
        scrollWidth: element.scrollWidth,
        clientWidth: element.clientWidth,
      });
    readMetrics();
    element.addEventListener('scroll', readMetrics, { passive: true });

    // Watch the content too: expanding a column changes scrollWidth without resizing the viewport.
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(readMetrics);
    observer?.observe(element);
    if (element.firstElementChild) observer?.observe(element.firstElementChild);

    return () => {
      element.removeEventListener('scroll', readMetrics);
      observer?.disconnect();
    };
  }, [scrollElementRef, scrollToken]);

  const scrollableWidth = Math.max(0, metrics.scrollWidth - metrics.clientWidth);
  if (scrollableWidth < 1) return null;

  const scrollToTrackOffset = (offset: number, travel: number) => {
    const element = scrollElementRef.current;
    if (!element || travel <= 0) return;
    element.scrollLeft = (Math.min(Math.max(offset, 0), travel) / travel) * scrollableWidth;
  };

  const getTravel = () => Math.max(0, (trackRef.current?.clientWidth ?? 0) - HANDLE_SIZE_PX);

  const dragHandle = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const travel = getTravel();
    const startClientX = event.clientX;
    const startScrollLeft = scrollElementRef.current?.scrollLeft ?? 0;
    const startOffset = travel > 0 ? (startScrollLeft / scrollableWidth) * travel : 0;

    const onPointerMove = (moveEvent: PointerEvent) => scrollToTrackOffset(startOffset + (moveEvent.clientX - startClientX), travel);
    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const jumpToPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    const trackLeft = trackRef.current?.getBoundingClientRect().left ?? 0;
    scrollToTrackOffset(event.clientX - trackLeft - HANDLE_SIZE_PX / 2, getTravel());
  };

  return (
    <div
      css={BAR_STYLE}
      style={{ paddingLeft: insetLeftPx, paddingRight: insetRightPx }}
      data-testid='progress-alt-scroll-bar'
    >
      <div
        ref={trackRef}
        css={TRACK_STYLE}
        role='scrollbar'
        aria-label='Scroll columns'
        aria-orientation='horizontal'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round((metrics.scrollLeft / scrollableWidth) * 100)}
        onPointerDown={jumpToPointer}
      >
        <span css={BEAM_STYLE} aria-hidden='true' />
        <div
          css={HANDLE_STYLE}
          style={{
            left: `calc(${(metrics.scrollLeft / scrollableWidth) * 100}% - ${(metrics.scrollLeft / scrollableWidth) * HANDLE_SIZE_PX}px)`,
          }}
          onPointerDown={dragHandle}
        />
      </div>
    </div>
  );
};
